import type {
  CameraPose,
  PresenceCameraConfig,
  PresenceSpace,
  RectBounds,
  SpawnPoint,
} from '@/world-prototype/types';
import { prefersReducedMotion } from '@/world-prototype/input/gestures';
import {
  clampToBounds,
  clampToPolygon,
  planWalkRoute,
  pushOutOfObstacles,
} from '@/world-prototype/movement/walkArea';

export type PresenceListener = (state: PresenceState) => void;

export type CharacterFacing = 'left' | 'right';

export interface PresenceState {
  characterX: number;
  characterY: number;
  destinationX: number | null;
  destinationY: number | null;
  walking: boolean;
  facing: CharacterFacing;
  camera: CameraPose;
  characterScale: number;
}

export interface PresenceControllerOptions {
  spawn?: SpawnPoint;
}

/** Framing from the view's responsive zoom — drives pan clamps + dead zone. */
export interface ExploreFraming {
  zoom: number;
  worldWidth: number;
  worldHeight: number;
  /** When true, use a soft dead zone before the camera follows. */
  followDeadZone: boolean;
  /**
   * Dead-zone size as a fraction of the visible viewport (full width/height).
   * Half-extents in world space = frac × visibleHalfExtent.
   */
  deadZoneViewportFracX?: number;
  deadZoneViewportFracY?: number;
}

/** Below this fraction of a step, a frame's movement counts as stalled. */
const STALL_PROGRESS_FRACTION = 0.05;
/** Consecutive stalled frames before the walk gives up (wedged on geometry). */
const MAX_STALLED_FRAMES = 8;
/** Fallback world-space dead-zone half-size when framing fracs are absent. */
const FOLLOW_DEAD_ZONE_X = 0.07;
const FOLLOW_DEAD_ZONE_Y = 0.055;

/**
 * Continuous presence movement + storybook follow camera for one illustrated space.
 * Click/tap the yard → walk there (clamped to walk polygon / bounds). Latest destination wins.
 */
export class PresenceController {
  private characterX: number;
  private characterY: number;
  private targetX: number;
  private targetY: number;
  private walking = false;
  private camera: CameraPose;
  private rafId: number | null = null;
  private lastTs: number | null = null;
  private readonly listeners = new Set<PresenceListener>();
  private space: PresenceSpace;
  private walkDirX = 0;
  private walkDirY = 0;
  private facing: CharacterFacing = 'right';
  private faceTowards: SpawnPoint | null = null;
  private stalledFrames = 0;
  /**
   * Remaining route waypoints (last one is the destination). Planned once
   * per walkTo — straight-line steering re-aims every frame and stalls
   * against obstacle footprints between character and target.
   */
  private waypoints: SpawnPoint[] = [];
  private pendingArrive: ((at: SpawnPoint) => void) | null = null;
  private exploreFraming: ExploreFraming | null = null;

  constructor(space: PresenceSpace, options?: PresenceControllerOptions) {
    this.space = space;
    const spawn = options?.spawn ?? space.start;
    const clamped = clampToWalkArea(spawn.x, spawn.y, space);
    this.characterX = clamped.x;
    this.characterY = clamped.y;
    this.targetX = this.characterX;
    this.targetY = this.characterY;
    this.camera = initialCamera(space, this.characterX, this.characterY);
  }

  getSpace(): PresenceSpace {
    return this.space;
  }

  getCharacterPosition(): SpawnPoint {
    return { x: this.characterX, y: this.characterY };
  }

  /**
   * Seed / snap the camera center in world coords (0–1), clamped to current
   * pan bounds. Does not move the character. Used for default composition
   * on enter and for coverage safety recenters.
   */
  setCameraCenter(centerX: number, centerY: number): void {
    const bounds = this.resolvePanBounds();
    this.camera = {
      ...this.camera,
      centerX: clamp(centerX, bounds.xMin, bounds.xMax),
      centerY: clamp(centerY, bounds.yMin, bounds.yMax),
    };
    this.emit();
  }

  /**
   * Update zoom-aware pan clamps so cover/follow cameras never show empty
   * space outside the artwork. Call from the view whenever explore zoom changes.
   */
  setExploreFraming(framing: ExploreFraming | null): void {
    this.exploreFraming = framing;
    // Re-clamp the live camera so a resize/orientation change stays valid.
    const bounds = this.resolvePanBounds();
    this.camera = {
      ...this.camera,
      centerX: clamp(this.camera.centerX, bounds.xMin, bounds.xMax),
      centerY: clamp(this.camera.centerY, bounds.yMin, bounds.yMax),
    };
    this.emit();
  }

  private resolvePanBounds(): RectBounds {
    const framing = this.exploreFraming;
    if (!framing || framing.zoom <= 0) {
      return this.space.camera.panBounds;
    }
    const halfW = Math.min(
      0.5,
      0.5 / (Math.max(0.01, framing.worldWidth) * framing.zoom),
    );
    const halfH = Math.min(
      0.5,
      0.5 / (Math.max(0.01, framing.worldHeight) * framing.zoom),
    );
    // Cover/follow: hard clamps from the visible window (full active world).
    // Never use viewport size as a walk bound — only as camera pan limits.
    if (framing.followDeadZone) {
      return {
        xMin: halfW,
        xMax: 1 - halfW,
        yMin: halfH,
        yMax: 1 - halfH,
      };
    }
    return this.space.camera.panBounds;
  }

  private resolveDeadZoneHalfExtents(): { deadX: number; deadY: number } {
    const framing = this.exploreFraming;
    if (!framing || !framing.followDeadZone) {
      return { deadX: FOLLOW_DEAD_ZONE_X, deadY: FOLLOW_DEAD_ZONE_Y };
    }
    const halfW = Math.min(
      0.5,
      0.5 / (Math.max(0.01, framing.worldWidth) * framing.zoom),
    );
    const halfH = Math.min(
      0.5,
      0.5 / (Math.max(0.01, framing.worldHeight) * framing.zoom),
    );
    const fracX = framing.deadZoneViewportFracX ?? 0.6;
    const fracY = framing.deadZoneViewportFracY ?? 0.55;
    return {
      deadX: Math.max(0.02, fracX * halfW),
      deadY: Math.max(0.02, fracY * halfH),
    };
  }

  getState(): PresenceState {
    return {
      characterX: this.characterX,
      characterY: this.characterY,
      destinationX: this.walking ? this.targetX : null,
      destinationY: this.walking ? this.targetY : null,
      walking: this.walking,
      facing: this.facing,
      camera: { ...this.camera },
      characterScale: this.camera.characterScale,
    };
  }

  /**
   * Walk to a point in world coords.
   * Outside the grass clamps to the nearest walkable edge (polygon or rect).
   * Optional onArrive fires once when the character stops at the destination.
   * Optional faceTowards turns the character toward a point on arrival
   * (e.g. an interactive object's position).
   */
  walkTo(
    x: number,
    y: number,
    options?: {
      onArrive?: (at: SpawnPoint) => void;
      faceTowards?: SpawnPoint;
    },
  ): boolean {
    this.pendingArrive = options?.onArrive ?? null;
    this.faceTowards = options?.faceTowards ?? null;
    this.stalledFrames = 0;
    const clamped = clampToWalkArea(x, y, this.space);
    this.targetX = clamped.x;
    this.targetY = clamped.y;
    const dx = this.targetX - this.characterX;
    const dy = this.targetY - this.characterY;
    const dist = Math.hypot(dx, dy);
    if (dist < 0.004) {
      this.waypoints = [];
      this.walking = false;
      this.applyArrivalFacing();
      this.emit();
      const arrive = this.pendingArrive;
      this.pendingArrive = null;
      arrive?.({ x: this.targetX, y: this.targetY });
      return true;
    }
    this.waypoints = planWalkRoute(
      { x: this.characterX, y: this.characterY },
      clamped,
      this.space.walkPolygon,
      this.space.walkObstacles,
    );
    this.walkDirX = dx / dist;
    this.walkDirY = dy / dist;
    this.updateFacingFromDir();
    this.walking = true;
    this.startLoop();
    this.emit();
    return true;
  }

  subscribe(listener: PresenceListener): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => {
      this.listeners.delete(listener);
    };
  }

  destroy(): void {
    this.stopLoop();
    this.listeners.clear();
    this.pendingArrive = null;
    this.faceTowards = null;
  }

  private startLoop(): void {
    if (this.rafId != null) return;
    this.lastTs = null;
    const tick = (ts: number) => {
      this.rafId = window.requestAnimationFrame(tick);
      if (this.lastTs == null) {
        this.lastTs = ts;
        this.update(1 / 60);
        return;
      }
      const dt = Math.min(0.05, Math.max(0, (ts - this.lastTs) / 1000));
      this.lastTs = ts;
      this.update(dt);
    };
    this.rafId = window.requestAnimationFrame(tick);
  }

  private stopLoop(): void {
    if (this.rafId != null) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.lastTs = null;
  }

  private update(dt: number): void {
    if (this.walking) {
      const waypoint = this.waypoints[0] ?? {
        x: this.targetX,
        y: this.targetY,
      };
      const dx = waypoint.x - this.characterX;
      const dy = waypoint.y - this.characterY;
      const dist = Math.hypot(dx, dy);
      const step = this.space.walkSpeed * dt;
      if (dist <= step || dist < 0.004) {
        if (this.waypoints.length > 1) {
          // Intermediate route waypoint — keep walking to the next one.
          this.characterX = waypoint.x;
          this.characterY = waypoint.y;
          this.waypoints.shift();
          this.stalledFrames = 0;
        } else {
          this.characterX = this.targetX;
          this.characterY = this.targetY;
          this.finishWalk(dt);
          return;
        }
      } else {
        this.walkDirX = dx / dist;
        this.walkDirY = dy / dist;
        this.updateFacingFromDir();
        const next = resolveWalkPoint(
          this.characterX + this.walkDirX * step,
          this.characterY + this.walkDirY * step,
          this.space,
        );
        const moved = Math.hypot(
          next.x - this.characterX,
          next.y - this.characterY,
        );
        this.characterX = next.x;
        this.characterY = next.y;

        // Wedged against geometry: stop cleanly instead of jittering forever.
        if (moved < step * STALL_PROGRESS_FRACTION) {
          this.stalledFrames += 1;
          if (this.stalledFrames >= MAX_STALLED_FRAMES) {
            this.finishWalk(dt);
            return;
          }
        } else {
          this.stalledFrames = 0;
        }
      }
    }

    this.camera = stepCamera(
      this.camera,
      this.characterX,
      this.characterY,
      this.walkDirX,
      this.walkDirY,
      this.walking,
      this.space.camera,
      dt,
      this.cameraStepOptions(),
    );

    this.emit();

    if (
      !this.walking &&
      cameraSettled(
        this.camera,
        this.characterX,
        this.characterY,
        this.space.camera,
        this.resolvePanBounds(),
      )
    ) {
      this.stopLoop();
    }
  }

  /** Shared arrival/stop: clears walk state, applies facing, fires onArrive. */
  private finishWalk(dt: number): void {
    this.walking = false;
    this.walkDirX = 0;
    this.walkDirY = 0;
    this.stalledFrames = 0;
    this.waypoints = [];
    this.applyArrivalFacing();
    const arrive = this.pendingArrive;
    this.pendingArrive = null;
    this.camera = stepCamera(
      this.camera,
      this.characterX,
      this.characterY,
      0,
      0,
      false,
      this.space.camera,
      dt,
      this.cameraStepOptions(),
    );
    this.emit();
    arrive?.({ x: this.targetX, y: this.targetY });
    if (
      cameraSettled(
        this.camera,
        this.characterX,
        this.characterY,
        this.space.camera,
        this.resolvePanBounds(),
      )
    ) {
      this.stopLoop();
    }
  }

  private cameraStepOptions(): {
    panBounds: RectBounds;
    followDeadZone: boolean;
    deadZoneX: number;
    deadZoneY: number;
  } {
    const { deadX, deadY } = this.resolveDeadZoneHalfExtents();
    return {
      panBounds: this.resolvePanBounds(),
      followDeadZone: Boolean(this.exploreFraming?.followDeadZone),
      deadZoneX: deadX,
      deadZoneY: deadY,
    };
  }

  private updateFacingFromDir(): void {
    if (Math.abs(this.walkDirX) > 0.02) {
      this.facing = this.walkDirX < 0 ? 'left' : 'right';
    }
  }

  private applyArrivalFacing(): void {
    if (!this.faceTowards) return;
    const dx = this.faceTowards.x - this.characterX;
    if (Math.abs(dx) > 0.002) {
      this.facing = dx < 0 ? 'left' : 'right';
    }
    this.faceTowards = null;
  }

  private emit(): void {
    const state = this.getState();
    for (const listener of this.listeners) {
      listener(state);
    }
  }
}

export { pointInBounds } from '@/world-prototype/movement/walkArea';

export function resolveCharacterScale(
  characterY: number,
  camera: PresenceCameraConfig,
): number {
  // High Y = near porch (foreground, larger). Low Y = deep yard (smaller).
  const span = camera.depthYNear - camera.depthYFar || 1;
  const t = clamp((characterY - camera.depthYFar) / span, 0, 1);
  return lerp(camera.depthScaleFar, camera.depthScaleNear, t);
}

export function stepCamera(
  current: CameraPose,
  characterX: number,
  characterY: number,
  dirX: number,
  dirY: number,
  walking: boolean,
  config: PresenceCameraConfig,
  dt: number,
  options?: {
    panBounds?: RectBounds;
    followDeadZone?: boolean;
    deadZoneX?: number;
    deadZoneY?: number;
  },
): CameraPose {
  const look = walking ? config.lookAhead : config.lookAhead * 0.25;
  let desiredX = characterX + dirX * look - config.framingBiasX;
  let desiredY = characterY + dirY * look * 0.65 - config.framingBiasY;

  // Soft dead zone: character can drift in the center before the camera moves.
  if (options?.followDeadZone) {
    const deadX = options.deadZoneX ?? FOLLOW_DEAD_ZONE_X;
    const deadY = options.deadZoneY ?? FOLLOW_DEAD_ZONE_Y;
    desiredX = applyDeadZone(current.centerX, desiredX, deadX);
    desiredY = applyDeadZone(current.centerY, desiredY, deadY);
  }

  const bounds = options?.panBounds ?? config.panBounds;
  // Ensure bounds are ordered (cover can lock an axis to a single point).
  const xMin = Math.min(bounds.xMin, bounds.xMax);
  const xMax = Math.max(bounds.xMin, bounds.xMax);
  const yMin = Math.min(bounds.yMin, bounds.yMax);
  const yMax = Math.max(bounds.yMin, bounds.yMax);

  const clampedX = clamp(desiredX, xMin, xMax);
  const clampedY = clamp(desiredY, yMin, yMax);

  const nearEdge =
    edgeProximity(desiredX, xMin, xMax) > 0.7 ||
    edgeProximity(desiredY, yMin, yMax) > 0.7;

  const smoothing = prefersReducedMotion()
    ? 0.04
    : nearEdge
      ? config.edgeSmoothing
      : config.followSmoothing;
  const alpha = 1 - Math.exp(-dt / Math.max(0.04, smoothing));

  return {
    centerX: lerp(current.centerX, clampedX, alpha),
    centerY: lerp(current.centerY, clampedY, alpha),
    characterScale: resolveCharacterScale(characterY, config),
  };
}

function applyDeadZone(
  current: number,
  desired: number,
  dead: number,
): number {
  const delta = desired - current;
  if (Math.abs(delta) <= dead) return current;
  return desired - Math.sign(delta) * dead;
}

function initialCamera(
  space: PresenceSpace,
  characterX: number,
  characterY: number,
): CameraPose {
  const config = space.camera;
  return {
    centerX: clamp(
      characterX - config.framingBiasX,
      config.panBounds.xMin,
      config.panBounds.xMax,
    ),
    centerY: clamp(
      characterY - config.framingBiasY,
      config.panBounds.yMin,
      config.panBounds.yMax,
    ),
    characterScale: resolveCharacterScale(characterY, config),
  };
}

function cameraSettled(
  camera: CameraPose,
  characterX: number,
  characterY: number,
  config: PresenceCameraConfig,
  panBounds: RectBounds = config.panBounds,
): boolean {
  const desiredX = clamp(
    characterX - config.framingBiasX,
    panBounds.xMin,
    panBounds.xMax,
  );
  const desiredY = clamp(
    characterY - config.framingBiasY,
    panBounds.yMin,
    panBounds.yMax,
  );
  return (
    Math.abs(camera.centerX - desiredX) < 0.0015 &&
    Math.abs(camera.centerY - desiredY) < 0.0015
  );
}

/** Soft approach feel comes from edgeSmoothing — bounds themselves stay hard. */
function edgeProximity(value: number, min: number, max: number): number {
  const span = max - min || 1;
  const dist = Math.min(Math.abs(value - min), Math.abs(value - max));
  return 1 - clamp(dist / (span * 0.2), 0, 1);
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function clampToWalkArea(
  x: number,
  y: number,
  space: PresenceSpace,
): SpawnPoint {
  return resolveWalkPoint(x, y, space);
}

function resolveWalkPoint(
  x: number,
  y: number,
  space: PresenceSpace,
): SpawnPoint {
  let point: SpawnPoint =
    space.walkPolygon && space.walkPolygon.length >= 3
      ? clampToPolygon(x, y, space.walkPolygon)
      : clampToBounds(x, y, space.walkBounds);
  point = pushOutOfObstacles(point.x, point.y, space.walkObstacles);
  // Pushing out of an obstacle near the fence can leave the walk polygon —
  // re-clamp once so destinations stay on valid grass.
  if (space.walkPolygon && space.walkPolygon.length >= 3) {
    point = clampToPolygon(point.x, point.y, space.walkPolygon);
    point = pushOutOfObstacles(point.x, point.y, space.walkObstacles);
  } else {
    point = clampToBounds(point.x, point.y, space.walkBounds);
    point = pushOutOfObstacles(point.x, point.y, space.walkObstacles);
  }
  return point;
}
